import React, { useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { Mail, Lock, ArrowRight } from 'lucide-react';
import { useAuth } from '@/lib/auth';

const Login: React.FC = () => {
  const location = useLocation();
  const { user, loading, signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const redirectTo = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ?? '/';

  if (!loading && user) {
    return <Navigate to={redirectTo} replace />;
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      await signIn(email, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Authentication failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block text-3xl font-bold font-poppins text-[#1A1A1A] hover:opacity-80 transition-opacity">
            Kipr
          </Link>
          <p className="text-[#555555] mt-2 text-[15px]">Sign in to continue training</p>
        </div>

        <div className="bg-white rounded-2xl border border-[#E8E8E8] shadow-[0_20px_70px_-28px_rgba(0,0,0,0.14)] p-7 sm:p-9">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="login-email" className="block text-sm font-medium text-[#555555] mb-1.5">
                Email
              </label>
              <div className="relative">
                <Mail
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#A3A3A3] pointer-events-none"
                  size={18}
                  strokeWidth={1.75}
                />
                <input
                  id="login-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                  className="w-full pl-10 pr-4 py-3 text-[15px] text-[#1A1A1A] placeholder:text-[#A3A3A3] bg-[#FAFAFA] border border-[#E0E0E0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#121212]/20 focus:border-[#121212] transition-shadow"
                  placeholder="you@example.com"
                />
              </div>
            </div>

            <div>
              <label htmlFor="login-password" className="block text-sm font-medium text-[#555555] mb-1.5">
                Password
              </label>
              <div className="relative">
                <Lock
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#A3A3A3] pointer-events-none"
                  size={18}
                  strokeWidth={1.75}
                />
                <input
                  id="login-password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                  minLength={8}
                  className="w-full pl-10 pr-4 py-3 text-[15px] text-[#1A1A1A] placeholder:text-[#A3A3A3] bg-[#FAFAFA] border border-[#E0E0E0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#121212]/20 focus:border-[#121212] transition-shadow"
                  placeholder="At least 8 characters"
                />
              </div>
              <div className="mt-2 text-right">
                <Link
                  to="/forgot-password"
                  className="text-sm font-medium text-[#1A1A1A] hover:underline underline-offset-4"
                >
                  Forgot password?
                </Link>
              </div>
            </div>

            {error && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-100/80 rounded-xl px-4 py-3 leading-snug">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="group w-full flex items-center justify-center gap-2 bg-[#121212] text-white py-3.5 rounded-xl text-[15px] font-semibold hover:bg-[#2A2A2A] disabled:opacity-50 disabled:pointer-events-none transition-colors"
            >
              {isSubmitting ? (
                'Signing in…'
              ) : (
                <>
                  Sign in
                  <ArrowRight
                    className="w-4 h-4 transition-transform group-hover:translate-x-0.5"
                    strokeWidth={2}
                  />
                </>
              )}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-[#555555]">
            Need an account?{' '}
            <Link
              to="/signup"
              state={location.state}
              className="font-semibold text-[#1A1A1A] hover:underline underline-offset-4"
            >
              Sign up
            </Link>
          </p>
          <p className="mt-4 text-center text-xs text-[#777777]">
            By continuing, you agree to our{' '}
            <Link to="/terms" className="underline hover:no-underline">
              Terms
            </Link>{' '}
            and{' '}
            <Link to="/privacy" className="underline hover:no-underline">
              Privacy Policy
            </Link>
            .
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
